"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  title: "Start a Basket",
  description: "Select your favorite restaurant in Toronto and start a shared order. Set a time limit for others to join your basket."
}, {
  title: "Nearby Matching",
  description: "Our smart algorithm instantly notifies hungry students and professionals nearby who want to split the delivery cost."
}, {
  title: "Split the Bill",
  description: "The app automatically calculates everyone's share. Pay only for your meal plus a tiny fraction of the service fees."
}, {
  title: "Enjoy Together",
  description: "Meet up at a convenient campus spot or get it delivered to your door. Fresh food, new friends, and major savings."
}];
export default function HowItWorks() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section data-section-id="167165" id="howhalforderworks" className="relative w-full py-[160px] bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-sans font-black text-foreground mb-6 tracking-tighter">
            How <span className="text-primary">HalfOrder</span> Works
          </h2>
          <p className="text-lg md:text-xl font-sans text-primary font-medium leading-relaxed uppercase tracking-widest">
            The smartest way to eat in the city.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {steps.map((step, i) => {
          const {
            ref: cardRef,
            isVisible: isCardVisible
          } = useRevealOnScroll({
            threshold: 0.1
          });
          return <div key={i} data-index={i} ref={cardRef} className={`group relative flex flex-col bg-card/20 backdrop-blur-[20px] border border-white/10 rounded-[32px] p-8 md:p-10 overflow-hidden transition-all duration-700 ease-out hover:bg-card/40 hover:border-primary/50 hover:shadow-2xl hover:shadow-primary/20 ${isCardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                {}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                {}
                <div className="mb-8 flex items-center justify-between">
                  <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-primary/20 border border-primary/30 text-primary font-mono text-2xl font-black shadow-lg shadow-primary/10">
                    {i + 1}
                  </div>
                  {i < steps.length - 1 && <ArrowRight className="w-6 h-6 text-muted-foreground/20 hidden lg:block group-hover:text-primary transition-colors duration-300 translate-x-4" />}
                </div>

                {}
                <div className="flex-1 flex flex-col relative z-10">
                  <h3 className="text-xl md:text-2xl font-sans font-black text-foreground mb-4 group-hover:text-primary transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="font-sans text-muted-foreground leading-relaxed text-base md:text-lg">
                    {step.description}
                  </p>
                </div>
              </div>;
        })}
        </div>
        
        <div className="mt-20 text-center opacity-40">
          <p className="text-sm font-sans tracking-widest uppercase">Trusted by thousands of students across Toronto</p>
        </div>
      </div>
    </section>;
}